// ResumeColumns.jsx
import React, { useState } from 'react';
import { CheckCircle, Download, History, MoreHorizontal, Trash2 } from 'lucide-react';

const ResumeColumns = ({
  resumeId,
  setResumeId,
  setModalOpen,
  handleDeleteResume,
  openRevisionHistory,
}) => {
  const [showDropdown, setShowDropdown] = useState({});

  const toggleDropdown = (id) => {
    setShowDropdown((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: 'numeric',
    });
    return (
      <>
        <div className="whitespace-nowrap">{dateStr}</div>
        <div className="whitespace-nowrap">{timeStr}</div>
      </>
    );
  };

  // Handle click events for dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.actions-dropdown')) {
        setShowDropdown({});
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Prevent dropdown clicks from propagating
  const handleDropdownClick = (e, id) => {
    e.stopPropagation();
    toggleDropdown(id);
  };

  return React.useMemo(
    () => [
      {
        header: 'Title',
        accessorKey: 'title',
        // Make this column take more space on mobile
        size: 150,
        cell: (info) => (
          <div className="flex flex-col space-y-2 min-w-0">
            <span className="font-medium text-gray-800 dark:text-gray-200 text-base md:text-lg truncate">
              {info.getValue()}
            </span>
            <div className="flex flex-wrap gap-2">
              {info.row.original.id !== resumeId ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      // The setResumeId will trigger our isResumeSwitch flag in DataContext
                      setResumeId(info.row.original.id);
                      setModalOpen(false);
                    }}
                    className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 focus:outline-none transition-colors duration-200"
                  >
                    <Download className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    Load
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => handleDropdownClick(e, info.row.original.id)}
                      className="actions-dropdown inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 focus:outline-none transition-colors duration-200"
                    >
                      <MoreHorizontal className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                      Actions
                    </button>
                    {showDropdown[info.row.original.id] && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                        <div className="py-1">
                          <button
                            type="button"
                            onClick={() => {
                              openRevisionHistory(info.row.original.id);
                              toggleDropdown(info.row.original.id);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          >
                            <History className="w-4 h-4 mr-2 text-amber-500 dark:text-amber-400" />
                            Saved Revisions
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              handleDeleteResume(info.row.original.id);
                              toggleDropdown(info.row.original.id);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          >
                            <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                            Delete Resume
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 rounded border border-emerald-200 dark:border-emerald-800">
                    <CheckCircle className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                    <span className="truncate">Currently Active</span>
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={(e) => handleDropdownClick(e, info.row.original.id)}
                      className="actions-dropdown inline-flex items-center px-2 py-1 md:px-3 md:py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 focus:outline-none transition-colors duration-200"
                    >
                      <MoreHorizontal className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                      Actions
                    </button>
                    {showDropdown[info.row.original.id] && (
                      <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 border border-gray-200 dark:border-gray-700">
                        <div className="py-1">
                          <button
                            type="button"
                            onClick={() => {
                              openRevisionHistory(info.row.original.id);
                              toggleDropdown(info.row.original.id);
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                          >
                            <History className="w-4 h-4 mr-2 text-amber-500 dark:text-amber-400" />
                            Saved Revisions
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            handleDeleteResume(info.row.original.id);
                            toggleDropdown(info.row.original.id);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center"
                        >
                          <Trash2 className="w-4 h-4 mr-2 text-red-500" />
                          Delete Resume
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        ),
      },
      {
        header: 'Last Modified',
        accessorKey: 'updated_at',
        // Hide on small screens, show on medium and up
        meta: {
          className: 'hidden md:table-cell',
        },
        cell: (info) => (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="items-center">{formatDate(info.getValue())}</div>
          </div>
        ),
      },
      {
        header: 'Created',
        accessorKey: 'created_at',
        // Hide on small screens, show on medium and up
        meta: {
          className: 'hidden md:table-cell',
        },
        cell: (info) => (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <div className="items-center">{formatDate(info.getValue())}</div>
          </div>
        ),
      },
    ],
    [resumeId, showDropdown, openRevisionHistory, handleDeleteResume, setResumeId, setModalOpen]
  );
};

export default ResumeColumns;
